import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {SORT} from '../../../../incase-domain/common/sort.enum';
import '../../../../styles/components/genericTable.scss';
import Button, {ButtonColorType} from '../../../../incase-domain/components/button/Button';
import Paginator, {PaginationPosition} from './pagination/Paginator';
import SearchBar from './search/SearchBar';
import Filter, {FilterData, FilterDataType, FilterDefinition} from './filters/Filter';
import EmptyTablePlaceholder from './placeholders/EmptyTablePlaceholder';
import Grid from './views/Grid';
import Table from './views/Table';
import {DataViewListStateProps} from '../../../../hooks/listParams';

export enum ViewMode {
    TABLE = 'solar:widget-linear',
    GRID = 'solar:hamburger-menu-linear',
}

export interface Column<T> {
    label?: string;
    key?: string;
    direction?: SORT;
    sortable?: boolean;
    body?: (row: T) => JSX.Element;
    width?: string;
    align?: 'left' | 'center' | 'right';
}

export interface SelectActions<T> {
    label: string;
    onClick: (selected: T[]) => void;
    icon?: string;
    loading?: boolean;
    disabled?: boolean;
    color?: ButtonColorType;
}

export interface HeaderActionProps {
    onClick: () => void;
    title: string;
    icon: string;
    dataCy?: string;
    loading?: boolean;
    disabled?: boolean;
}

export interface PaginationData {
    currentPage: number;
    pageSize: number;
    itemsCount: number;
}

export interface TableDataProps<T> {
    columns: Column<T>[];
    hideHeader?: boolean;
    view: ViewMode.TABLE;
}

export interface GridDataProps<T> {
    renderItem: (row: T, loading?: boolean, onClick?: (row: T) => void, isSelected?: boolean) => JSX.Element;
    columnsNumber?: number;
    columnsGap?: string;
    rowsGap?: string;
    view: ViewMode.GRID;
}

export type ViewConfig<T> = TableDataProps<T> | GridDataProps<T>;

interface DataViewProps<T> extends DataViewListStateProps {
    initialView?: ViewMode;
    onViewChange?: (view: ViewMode) => void;
    views: ViewConfig<T>[];
    title?: string;
    rows: T[];
    extraRow?: Record<string, JSX.Element>;
    rowIdentifier: (row: T) => string | number;
    onSelected?: (items: T[]) => void;
    loading?: boolean;
    selectActions?: SelectActions<T>[];
    onRowClick?: (row: T) => void;
    paginationData?: PaginationData;
    enableSearch?: boolean;
    headerActionPrimary?: HeaderActionProps;
    headerActionSecondary?: HeaderActionProps;
    headerActionTertiary?: HeaderActionProps;
    inlineTitle?: boolean;
    selectedItems?: T[];
    hideToolbar?: boolean;
    paginationPosition?: PaginationPosition;
    filterDefinitions?: FilterDefinition[];
    listDroppableId?: string;
    draggableIdOf?: (row: T) => string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function DataView<T extends Record<string, any> | null>(props: DataViewProps<T>): JSX.Element {
    const {
        initialView,
        onViewChange,
        views,
        title,
        rows,
        extraRow,
        rowIdentifier,
        onSelected,
        loading,
        selectActions,
        onRowClick,
        paginationData,
        enableSearch,
        headerActionPrimary,
        headerActionSecondary,
        headerActionTertiary,
        inlineTitle,
        selectedItems,
        hideToolbar,
        paginationPosition = PaginationPosition.TOP,
        filterDefinitions,
        search = '',
        filterValues = {},
        sortBy = {},
        listDroppableId,
        draggableIdOf,
        onPageChange,
        onSearchChange,
        onFiltersChange,
        onSortChange,
    } = props;

    const {t} = useTranslation();

    const availableViews = React.useMemo(() => views.map((v) => v.view), [views]);

    const [view, setView] = React.useState<ViewMode>(initialView || availableViews[0]);
    const [selectedRows, setSelectedRows] = useState<{[key: string | number]: T}>({});

    useEffect(() => {
        if (selectedItems?.length === 0) {
            setSelectedRows({});
        } else if (selectedItems?.length) {
            const newSelectedRows = selectedItems.reduce((acc, item) => {
                acc[rowIdentifier(item)] = item;
                return acc;
            }, {} as Record<string | number, T>);

            setSelectedRows(newSelectedRows);
        }
    }, [selectedItems, rowIdentifier]);

    useEffect(() => {
        const existingSelectedRows = Object.values(selectedRows).filter((selectedRow) =>
            rows.find((row) => rowIdentifier(row) === rowIdentifier(selectedRow))
        );

        setSelectedRows(
            existingSelectedRows.reduce((acc, item) => {
                acc[rowIdentifier(item)] = item;
                return acc;
            }, {} as Record<string | number, T>)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    const cfg = views.find((v) => v.view === view) || views[0];

    function nextDataView(current: ViewMode): ViewMode {
        const dataViews = views.map((v) => v.view);
        const i = dataViews.indexOf(current);
        const nextView = dataViews[(i + 1) % dataViews.length] ?? dataViews[0];
        onViewChange?.(nextView);
        return nextView;
    }

    function numberOfSelectedRows(): number {
        return Object.keys(selectedRows).length;
    }

    function clearSelectedRows(): void {
        const updatedSelectedRows = {...selectedRows};

        Object.keys(updatedSelectedRows).forEach((key) => {
            delete updatedSelectedRows[key];
        });

        setSelectedRows(updatedSelectedRows);
    }

    function handleSelectAll(): void {
        const updatedSelectedRows = {...selectedRows};

        if (numberOfSelectedRows() === rows.length) {
            Object.keys(updatedSelectedRows).forEach((key) => {
                delete updatedSelectedRows[key];
            });
        } else {
            rows.forEach((row) => {
                updatedSelectedRows[rowIdentifier(row)] = row;
            });
        }

        setSelectedRows(updatedSelectedRows);
        onSelected?.(Object.values(updatedSelectedRows));
    }

    function handleOnSelectedItem(item: T): void {
        const rowId = rowIdentifier(item);
        const updatedSelectedRows = {...selectedRows};

        if (updatedSelectedRows[rowId]) {
            delete updatedSelectedRows[rowId];
        } else {
            updatedSelectedRows[rowId] = item;
        }

        setSelectedRows(updatedSelectedRows);
        onSelected?.(Object.values(updatedSelectedRows));
    }

    function handleFilterChange(changedFilters: FilterData[]): void {
        const mappedFilters: Record<string, string> = changedFilters.reduce((acc, f) => {
            acc[f.key] = f.initialValue !== undefined ? f.initialValue.toString() : '';
            return acc;
        }, {} as Record<string, string>);

        onFiltersChange?.(mappedFilters);
    }

    const filters = useMemo<FilterData[]>(() => {
        if (!filterDefinitions) {
            return [];
        }

        return filterDefinitions.map((filterDefinition) => {
            const value = filterValues[filterDefinition.key];

            if (filterDefinition.type === FilterDataType.BOOLEAN) {
                return {
                    ...filterDefinition,
                    initialValue: value === 'true' || (value === 'false' ? false : undefined),
                };
            }

            return {
                ...filterDefinition,
                initialValue: value ? value.split(',').filter(Boolean) : [],
            };
        });
    }, [filterDefinitions, filterValues]);

    return (
        <>
            {title && !inlineTitle && <div className="c-genericTable__title">{title}</div>}
            {!hideToolbar && (
                <div className="c-genericTable__toolbar">
                    {title && inlineTitle && <div className="c-genericTable__title_inline">{title}</div>}
                    {enableSearch && <SearchBar value={search} onSearch={(value) => onSearchChange?.(value)} />}
                    {filterDefinitions && <Filter filters={filters} onChange={handleFilterChange} />}
                    {views.length > 1 && <Button color="frost" square onClick={() => setView(nextDataView(view))} leading={view} />}
                    <div style={{flex: '1'}} />
                    {paginationData && rows.length > 0 && paginationPosition === PaginationPosition.TOP && (
                        <Paginator data={paginationData} onPageChange={(value) => onPageChange?.(value)} />
                    )}
                    <div style={{flex: '1'}} />

                    {headerActionTertiary && (
                        <Button
                            leading={headerActionTertiary.icon}
                            variant="soft"
                            color="primary"
                            size="xs"
                            onClick={headerActionTertiary.onClick}
                            loading={headerActionTertiary.loading}
                            disabled={headerActionTertiary.disabled}
                        >
                            {headerActionTertiary.title}
                        </Button>
                    )}
                    {headerActionSecondary && (
                        <Button
                            leading={headerActionSecondary.icon}
                            variant="soft"
                            color="primary"
                            size="xs"
                            onClick={headerActionSecondary.onClick}
                            loading={headerActionSecondary.loading}
                            disabled={headerActionSecondary.disabled}
                        >
                            {headerActionSecondary.title}
                        </Button>
                    )}
                    {headerActionPrimary && (
                        <Button
                            leading={headerActionPrimary.icon}
                            variant="solid"
                            color="secondary"
                            size="xs"
                            onClick={headerActionPrimary.onClick}
                            dataCy={headerActionPrimary.dataCy}
                            loading={headerActionPrimary.loading}
                            disabled={headerActionPrimary.disabled}
                        >
                            {headerActionPrimary.title}
                        </Button>
                    )}
                </div>
            )}
            {cfg.view === ViewMode.TABLE ? (
                <Table
                    columns={cfg.columns}
                    rows={rows}
                    extraRow={extraRow}
                    rowIdentifier={rowIdentifier}
                    loading={loading}
                    onRowClick={onRowClick}
                    sortBy={sortBy}
                    onSortChange={onSortChange}
                    hideHeader={cfg.hideHeader}
                    enableSelect={!!selectActions}
                    onSelectedItem={handleOnSelectedItem}
                    onSelectAll={handleSelectAll}
                    selectedRows={selectedRows}
                    listDroppableId={listDroppableId}
                    draggableIdOf={draggableIdOf}
                />
            ) : (
                <Grid
                    rowIdentifier={rowIdentifier}
                    rows={rows}
                    rowsGap={cfg.rowsGap}
                    columnsGap={cfg.columnsGap}
                    loading={loading}
                    columnsNumber={cfg.columnsNumber}
                    renderItem={cfg.renderItem}
                    selectedRows={selectedRows}
                    enableSelect={!!selectActions}
                    onSelectedItem={handleOnSelectedItem}
                    listDroppableId={listDroppableId}
                    draggableIdOf={draggableIdOf}
                />
            )}

            {rows.length === 0 && !loading && !extraRow && <EmptyTablePlaceholder />}

            <div className="c-genericTable__footer">
                {paginationData && rows.length > 0 && paginationPosition === PaginationPosition.BOTTOM && (
                    <Paginator data={paginationData} onPageChange={(value) => onPageChange?.(value)} />
                )}
            </div>

            {selectActions && numberOfSelectedRows() > 0 && (
                <div className="c-genericTable__select">
                    <div className="c-genericTable__select_header">
                        {`${numberOfSelectedRows()} ${t('genericTable.itemsSelected')}`}
                        <Button leading="ph:x-bold" onClick={clearSelectedRows} variant="ghost" size="xs" color="gray_400" square />
                    </div>
                    <div className="c-genericTable__select_buttons">
                        {selectActions.map((el) => (
                            <Button
                                key={`select-action-btn-${el.label}`}
                                leading={el.icon}
                                onClick={() => el.onClick(Object.values(selectedRows))}
                                loading={el.loading}
                                disabled={el.disabled}
                                color={el.color}
                                size="sm"
                            >
                                {el.label}
                            </Button>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}

export default DataView;
